import type {
  ClientCost,
  CostOrigin,
  CostSplitUsd,
  CostTokens,
  ModelCost,
  WorkflowCost,
} from './api';
import { Format } from './format';

/**
 * Uma linha da tabela de custos, vindo de cliente, workflow ou modelo.
 *
 * As três origens têm o mesmo corpo (chamadas, tokens, faixas, total) e diferem
 * só em como se chamam e em quem pode não ter preço. Sem esta peça a tabela era
 * escrita três vezes no template, e um ajuste de coluna tinha de ser feito nas três.
 */
interface CostRowFields {
  key: string;
  name: string;
  subtitle: string | null;
  isPriced: boolean;
  unpricedCalls: number;
  /** `null` na visão por modelo, que agrega clientes e não é gasto de ninguém. */
  origin: CostOrigin | null;
  ownerTenantId: string;
  runs: number;
  calls: number;
  tokens: CostTokens;
  split: CostSplitUsd;
  usd: number;
  brl: number;
  children: CostRow[];
  periodBrl: number;
  largestBrl: number;
}

export class CostRow {
  private constructor(private readonly fields: CostRowFields) {}

  static fromClient(cost: ClientCost): CostRow {
    return new CostRow({
      ...CostRow.blank(),
      key: cost.tenantId,
      name: cost.name ?? cost.tenantId,
      ownerTenantId: cost.tenantId,
      unpricedCalls: cost.unpricedCalls,
      runs: cost.runs,
      calls: cost.calls,
      tokens: cost.tokens,
      split: cost.split,
      usd: cost.usd,
      brl: cost.brl,
    });
  }

  static fromWorkflow(cost: WorkflowCost): CostRow {
    return new CostRow({
      ...CostRow.blank(),
      key: `${cost.tenantId}:${cost.workflowDefinitionId ?? `${cost.origin}:${cost.slug ?? ''}`}`,
      name: cost.name ?? cost.slug ?? '(sem definição)',
      subtitle: [cost.slug, cost.tenantName].filter(Boolean).join(' · ') || null,
      origin: cost.origin,
      ownerTenantId: cost.tenantId,
      unpricedCalls: cost.unpricedCalls,
      runs: cost.runs,
      calls: cost.calls,
      tokens: cost.tokens,
      split: cost.split,
      usd: cost.usd,
      brl: cost.brl,
    });
  }

  static fromModel(cost: ModelCost): CostRow {
    return new CostRow({
      ...CostRow.blank(),
      key: `${cost.provider}|${cost.model}`,
      name: cost.model ?? '(desconhecido)',
      subtitle: cost.provider ?? null,
      isPriced: cost.priced,
      unpricedCalls: cost.priced ? 0 : cost.calls,
      runs: cost.runs,
      calls: cost.calls,
      tokens: cost.tokens,
      split: cost.split,
      usd: cost.usd,
      brl: cost.brl,
    });
  }

  private static blank(): CostRowFields {
    return {
      key: '',
      name: '',
      subtitle: null,
      isPriced: true,
      unpricedCalls: 0,
      origin: null,
      ownerTenantId: '',
      runs: 0,
      calls: 0,
      tokens: { prompt: 0, cached: 0, completion: 0, reasoning: 0 },
      split: { inputCacheMiss: 0, inputCacheHit: 0, output: 0 },
      usd: 0,
      brl: 0,
      children: [],
      periodBrl: 0,
      largestBrl: 0,
    };
  }

  get key(): string {
    return this.fields.key;
  }

  get name(): string {
    return this.fields.name;
  }

  get subtitle(): string | null {
    return this.fields.subtitle;
  }

  get isPriced(): boolean {
    return this.fields.isPriced;
  }

  get usd(): number {
    return this.fields.usd;
  }

  get children(): CostRow[] {
    return this.fields.children;
  }

  /** Abre a linha nos workflows que a compõem, do mais caro para o mais barato. */
  withChildren(children: CostRow[]): CostRow {
    return new CostRow({ ...this.fields, children });
  }

  /** Ancora a linha no total do período, para ela saber o próprio peso.
   *  Desce para os filhos: a fatia da linha filha é sobre o mesmo total. */
  /** A mesma linha somando SÓ estes filhos. Existe para o filtro por origem: com
   *  metade dos workflows escondidos, o total do cliente tem de ser o dos que
   *  ficaram, senão a linha anuncia um valor que não bate com o que está embaixo. */
  rebuiltFrom(children: CostRow[]): CostRow {
    const somado = children.reduce(
      (acumulado, child) => ({
        runs: acumulado.runs + child.runsCount,
        calls: acumulado.calls + child.callsCount,
        unpricedCalls: acumulado.unpricedCalls + child.unpricedCalls,
        usd: acumulado.usd + child.usd,
        brl: acumulado.brl + child.totalBrl,
        tokens: {
          prompt: acumulado.tokens.prompt + child.fields.tokens.prompt,
          cached: acumulado.tokens.cached + child.fields.tokens.cached,
          completion: acumulado.tokens.completion + child.fields.tokens.completion,
          reasoning: acumulado.tokens.reasoning + child.fields.tokens.reasoning,
        },
        split: {
          inputCacheMiss: acumulado.split.inputCacheMiss + child.missUsd,
          inputCacheHit: acumulado.split.inputCacheHit + child.hitUsd,
          output: acumulado.split.output + child.outputUsd,
        },
      }),
      {
        runs: 0,
        calls: 0,
        unpricedCalls: 0,
        usd: 0,
        brl: 0,
        tokens: { prompt: 0, cached: 0, completion: 0, reasoning: 0 },
        split: { inputCacheMiss: 0, inputCacheHit: 0, output: 0 },
      },
    );
    return new CostRow({ ...this.fields, ...somado, children });
  }

  /** A escala em que esta linha é desenhada: o total do período dá a fatia em
   *  porcento, e a maior linha da tabela dá o comprimento da barra. */
  withScale(scale: { periodBrl: number; largestBrl: number }): CostRow {
    return new CostRow({
      ...this.fields,
      children: this.fields.children.map((child) => child.withScale(scale)),
      periodBrl: scale.periodBrl,
      largestBrl: scale.largestBrl,
    });
  }

  /** Os números crus por trás de cada coluna, para a tabela poder ordenar por
   *  qualquer uma delas. São perguntas de grandeza, não estrutura interna: quem
   *  pergunta quer comparar duas linhas, não remontar a linha. */
  get callsCount(): number {
    return this.fields.calls;
  }

  get runsCount(): number {
    return this.fields.runs;
  }

  get totalBrl(): number {
    return this.fields.brl;
  }

  get averageBrl(): number {
    if (!this.isPriced || this.fields.runs <= 0) return 0;
    return this.fields.brl / this.fields.runs;
  }

  get cacheHitRate(): number {
    if (this.fields.tokens.prompt <= 0) return 0;
    return this.fields.tokens.cached / this.fields.tokens.prompt;
  }

  get promptTokens(): number {
    return this.fields.tokens.prompt;
  }

  get cachedTokens(): number {
    return this.fields.tokens.cached;
  }

  get completionTokens(): number {
    return this.fields.tokens.completion;
  }

  get reasoningTokens(): number {
    return this.fields.tokens.reasoning;
  }

  get unpricedCalls(): number {
    return this.fields.unpricedCalls;
  }

  get missUsd(): number {
    return this.fields.split.inputCacheMiss;
  }

  get hitUsd(): number {
    return this.fields.split.inputCacheHit;
  }

  get outputUsd(): number {
    return this.fields.split.output;
  }

  /** Para onde o clique no nome leva, ou nulo quando a linha não tem detalhe: a
   *  visão por modelo agrega clientes diferentes, e o replay de teste não tem
   *  execução por trás — clicar levava a "sem gasto neste período". */
  get detailRoute(): { scope: 'cliente' | 'workflow'; id: string } | null {
    if (this.fields.origin === 'test') return null;
    if (!this.fields.origin) {
      return this.fields.ownerTenantId ? { scope: 'cliente', id: this.fields.ownerTenantId } : null;
    }
    return { scope: 'workflow', id: this.key };
  }

  /**
   * Comprimento da barra, de 0 a 100, medido contra a MAIOR linha da tabela e
   * não contra o total do período. Com 30 clientes na tela a maior fica em 40%
   * do período e a mediana em 0,3%: contra o total, todas viram o mesmo risco
   * invisível. O piso de 2 mantém visível a linha que gastou algo.
   */
  get barPercent(): number {
    if (this.fields.largestBrl <= 0 || this.fields.brl <= 0) return 0;
    return Math.max(2, Math.min(100, (this.fields.brl / this.fields.largestBrl) * 100));
  }

  get shareLabel(): string {
    return Format.percent(this.fields.brl, this.fields.periodBrl);
  }

  get hasChildren(): boolean {
    return this.children.length > 0;
  }

  /** Replay de comparação nosso: não é gasto de cliente nenhum, e sai da conta
   *  quando se quer saber quanto a operação custou de verdade. */
  get isOurTest(): boolean {
    return this.fields.origin === 'test';
  }

  /** Rotina nossa, não atendimento de cliente: relatório diário e priorização
   *  de carteira gastam no nome do cliente sem ninguém ter conversado com ele. */
  get isInternalRoutine(): boolean {
    return this.fields.origin !== null && this.fields.origin !== 'workflow';
  }

  /** Quanto desta linha é rotina interna, uma parte por rotina, para a conta
   *  aparecer sem abrir a linha. Lista vazia quando não há — a ausência não vira
   *  "$0", que se lê como se houvesse. */
  private get internalRoutineLabels(): string[] {
    return this.children
      .filter((child) => child.isInternalRoutine && child.usd > 0)
      .map((child) => `${child.name} ${Format.usd(child.usd)}`);
  }

  /** O que a linha do cliente diz sem ser aberta: quantos workflows e, se houver,
   *  quanto do gasto é rotina nossa em vez de atendimento. Linha sem nenhum
   *  atendimento — a dos nossos replays — omite o contador: "0 workflow(s)" lê
   *  como falha, e ali é o esperado. */
  get summaryLabel(): string {
    const atendimento = this.children.filter((child) => !child.isInternalRoutine).length;
    const partes = atendimento > 0 ? [`${atendimento} workflow(s)`] : [];
    return [...partes, ...this.internalRoutineLabels].join(' · ');
  }

  get callsLabel(): string {
    return Format.count(this.fields.calls);
  }

  /** O que uma rodada do workflow custa em média. É por RODADA, não por chamada:
   *  uma rodada gasta em várias chamadas (geração, juiz, tool). */
  get averageLabel(): string {
    if (!this.isPriced || this.fields.runs <= 0) return '—';
    return Format.brl(this.fields.brl / this.fields.runs);
  }

  /** Quanto da saída foi o modelo pensando em vez de responder. A saída é a
   *  faixa mais cara, então é onde o desperdício custa mais. */
  get reasoningLabel(): string {
    if (this.fields.tokens.completion <= 0) return '—';
    return Format.percent(this.fields.tokens.reasoning, this.fields.tokens.completion);
  }

  get cacheHitLabel(): string {
    return Format.percent(this.fields.tokens.cached, this.fields.tokens.prompt);
  }

  get missLabel(): string {
    return this.priceLabel(Format.band(this.fields.split.inputCacheMiss));
  }

  get hitLabel(): string {
    return this.priceLabel(Format.band(this.fields.split.inputCacheHit));
  }

  get outputLabel(): string {
    return this.priceLabel(Format.band(this.fields.split.output));
  }

  get usdLabel(): string {
    return this.priceLabel(Format.usd(this.usd));
  }

  get brlLabel(): string {
    return this.priceLabel(Format.brl(this.fields.brl));
  }

  private priceLabel(value: string): string {
    if (!this.isPriced) return '—';
    return value;
  }
}
